import { UserCoverageSummary } from '@ampsec/platform-client';
import { FaExclamationTriangle } from '@react-icons/all-files/fa/FaExclamationTriangle';
import { ToolingCoverageTooltip } from 'Components/molecules/ToolingCoverageTooltip';
import { GridContext } from '../AgGridCompliance.config';
import { formatCsvTooltipFields, type CsvTooltipField } from '../../AgGridBase/exportCsv';

interface AttributionStatusCellRendererProps {
  data: UserCoverageSummary;
  context: GridContext;
}

/** One tooltip row per SaaS asset: `<provider> - <asset>` / the assignment (or conflict) message. */
export type AttributionTooltipRow = CsvTooltipField & {
  value: string;
  isConflicting: boolean;
};

const toAssignmentLabel = (saasAsset: any): string => {
  const ownerEmail = (saasAsset.ownerEmail ?? '').trim();
  if (ownerEmail) {
    return ownerEmail;
  }
  return 'Unassigned';
};

const toConflictAssignmentMessage = (saasAsset: any): string => {
  const assignmentLabel = toAssignmentLabel(saasAsset);
  if (assignmentLabel === 'Unassigned') {
    return 'Conflicting: Asset is unassigned';
  }
  return `Conflicting: Asset assigned to ${assignmentLabel}`;
};

const toConnectorLabel = (saasAsset: any, context: GridContext): string => {
  const connector = context.allConnectors?.[saasAsset.cid];
  const providerDisplayValue = connector?.provider?.displayValue;
  if (!providerDisplayValue) {
    return saasAsset.cid;
  }
  return context.providerNameIntegrationMap?.[providerDisplayValue]?.name ?? providerDisplayValue;
};

const hasNegativeSaasAsset = (asset: any): boolean =>
  ((asset.saasAssets ?? []) as any[]).some((saasAsset: any) => saasAsset.attributionStatus === 'NEGATIVE');

/**
 * The rows the attribution tooltip renders, in the same order and formatting the cell shows: one
 * per SaaS asset, then one generic row per asset-level conflict. Shared with the CSV export so the
 * exported attribution detail cannot drift from the UI; the tooltip itself is only rendered when a
 * conflict exists.
 */
export function getAttributionTooltipRows(row: UserCoverageSummary, context: GridContext): AttributionTooltipRow[] {
  const assets = Array.isArray(row.assets) ? row.assets : [];
  const saasAssets = assets.flatMap((asset: any) =>
    ((asset.saasAssets ?? []) as any[]).map((saasAsset: any) => ({
      ...saasAsset,
      parentAsset: asset.displayValue,
    }))
  );

  const saasAssetRows = saasAssets.map((saasAsset: any) => {
    const isConflicting = saasAsset.attributionStatus === 'NEGATIVE';
    const label = `${toConnectorLabel(saasAsset, context)} - ${saasAsset.displayValue || saasAsset.parentAsset || 'Asset'}`;
    const value = isConflicting ? toConflictAssignmentMessage(saasAsset) : 'No conflict';
    return { label, value, isConflicting };
  });
  // Only surface an asset-level 'Conflicting attribution' row when the asset's conflict is not already
  // represented by one of its own negative saasAsset rows, to avoid duplicating the same conflict.
  const assetRows = assets
    .filter((asset: any) => asset.attributionStatus === 'NEGATIVE' && !hasNegativeSaasAsset(asset))
    .map((asset: any) => ({ label: asset.displayValue || 'Asset', value: 'Conflicting attribution', isConflicting: true }));

  return [...saasAssetRows, ...assetRows];
}

/**
 * The CSV tooltip cell of the attribution column: every per-SaaS-asset row the tooltip renders,
 * including the no-conflict ones (the cell itself only renders the alert when a conflict exists).
 */
export function getAttributionTooltipExport(row: UserCoverageSummary, context: GridContext): string {
  return formatCsvTooltipFields(getAttributionTooltipRows(row, context));
}

const AttributionStatusCellRenderer = ({ data: row, context }: AttributionStatusCellRendererProps) => {
  const tooltipRows = getAttributionTooltipRows(row, context);
  if (!tooltipRows.some((tooltipRow) => tooltipRow.isConflicting)) {
    return null;
  }

  return (
    <div className="coverage-cell coverage-cell--attribution" data-testid="attribution-status-negative">
      <ToolingCoverageTooltip placement="top">
        <ToolingCoverageTooltip.Trigger>
          <div className="coverage-cell__attribution-icon-wrapper">
            <FaExclamationTriangle className="coverage-cell__attribution-alert" size={20} />
          </div>
        </ToolingCoverageTooltip.Trigger>
        <ToolingCoverageTooltip.Header
          icon={<FaExclamationTriangle className="coverage-cell__attribution-alert" size={20} />}
          label="Attribution conflicts"
        />
        {tooltipRows.map((tooltipRow, index) => (
          <ToolingCoverageTooltip.Row
            key={`${index}-${tooltipRow.label}`}
            label={tooltipRow.label}
            value={tooltipRow.value}
            status={tooltipRow.isConflicting ? 'warn' : undefined}
          />
        ))}
      </ToolingCoverageTooltip>
    </div>
  );
};

export default AttributionStatusCellRenderer;
